import {
  type ElementType,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from 'react';

import { classFilter } from '@shieldpay/theme-provider-ui/utilities';
import { noop } from '@shieldpay/utility-functions-ui/noop';

import { Box } from '../box/box';
import { Text } from '../text/text';
import {
  type TooltipAriaDisabledProps,
  TooltipAriaDisabled,
} from '../tooltip/tooltip-aria-disabled';

import * as styles from './action-menu.css';

export type Size = keyof typeof paddingStyles;

export type Variant = keyof typeof styles.variants;

interface ActionMenuItemProps {
  children: ReactNode;
  disabled?: boolean;
  'aria-disabled'?: boolean;
  size?: Size;
  variant?: Variant;
  highlight?: boolean;
  hide?: boolean;
  LeftIcon?: ElementType;
  RightIcon?: ElementType;
  ref?: Ref<HTMLButtonElement>;
  className?: string;
}

export type ToOrHref =
  | {
      /**
       * the to prop is used when we want the link to work as a router link
       */
      to: string;
      NavLink: ElementType;
      href?: never;
      preventScrollReset?: boolean;
      // https://reactrouter.com/en/main/components/link#relative
      // 'route' is react-router default
      relative?: 'route' | 'path';
    }
  | {
      /**
       * the href prop is used when we want the link to work outside our app
       */
      href: string;
      NavLink?: never;
      to?: never;
      preventScrollReset?: never;
      relative?: never;
    };

export type ActionMenuItemDisabledProps = {
  children: ReactNode;
  size?: Size;
  LeftIcon?: ElementType;
  RightIcon?: ElementType;
  ref?: Ref<HTMLButtonElement>;
};

export type ActionMenuItemLinkProps = ActionMenuItemProps & ToOrHref;

export interface ActionMenuItemButtonProps extends ActionMenuItemProps {
  onClick: (e: MouseEvent<HTMLElement>) => void;
}

const paddingStyles = {
  medium: 'baseNeg3',
  large: 'basePos1',
} as const;

const TEST_IDS = {
  LEFT_ICON: 'action-menu-left-icon',
  RIGHT_ICON: 'action-menu-right-icon',
} as const;

const Inner = ({
  LeftIcon,
  RightIcon,
  children,
}: Pick<ActionMenuItemButtonProps, 'LeftIcon' | 'RightIcon' | 'children'>) => (
  <>
    {LeftIcon ? <LeftIcon data-testid={TEST_IDS.LEFT_ICON} /> : null}
    <Text
      truncate // make sure items stay on one line
      color="currentColor"
    >
      {children}
    </Text>
    {RightIcon ? <RightIcon data-testid={TEST_IDS.RIGHT_ICON} /> : null}
  </>
);

export const MenuItemButton = ({
  children,
  disabled,
  'aria-disabled': ariaDisabled,
  LeftIcon,
  onClick,
  RightIcon,
  size = 'medium',
  variant = 'default',
  highlight,
  hide = false,
  ref,
  className,
  ...props
}: ActionMenuItemButtonProps) => {
  return !hide ? (
    <Box
      data-highlighted={highlight}
      alignContent={{ x: 'left' }}
      className={classFilter([
        styles.menuItem,
        disabled ? styles.disabled : styles.variants[variant],
        ariaDisabled && styles.ariaDisabled,
        className,
      ])}
      aria-disabled={ariaDisabled}
      Component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      padding={{ x: 'base', y: paddingStyles[size] }}
      ref={ref}
      role="menuitem"
      rounded
      spacing="baseNeg2"
      stack="row"
      {...props}
    >
      <Inner LeftIcon={LeftIcon} RightIcon={RightIcon}>
        {children}
      </Inner>
    </Box>
  ) : null;
};

export const MenuItemLink = ({
  children,
  disabled,
  'aria-disabled': ariaDisabled,
  LeftIcon,
  preventScrollReset,
  RightIcon,
  size = 'medium',
  to,
  variant = 'default',
  highlight = false,
  hide = false,
  ref,
  NavLink,
  className,
  href,
  ...props
}: ActionMenuItemLinkProps) => {
  return !hide ? (
    <Box
      data-highlighted={highlight}
      alignContent={{ x: 'left' }}
      className={classFilter([
        styles.menuItem,
        disabled ? styles.disabled : styles.variants[variant],
        ariaDisabled && styles.ariaDisabled,
        className,
      ])}
      aria-disabled={ariaDisabled}
      Component={disabled ? 'span' : to ? NavLink : 'a'}
      padding={{ x: 'base', y: paddingStyles[size] }}
      ref={ref}
      role="menuitem"
      rounded
      spacing="baseNeg2"
      stack="row"
      variant="inline-flex"
      target={to ? undefined : '_blank'}
      {...(disabled
        ? {
            // if disabled, we don't want to pass some props
            // because we get react warning passing them to a span
          }
        : to
          ? {
              to,
              preventScrollReset,
            }
          : {
              href,
            })}
      {...props}
    >
      <Inner LeftIcon={LeftIcon} RightIcon={RightIcon}>
        {children}
      </Inner>
    </Box>
  ) : null;
};

export const MenuItemDisabled = ({
  children,
  variant = 'solid',
  LeftIcon,
  RightIcon,
  size = 'medium',
  ref,
  tooltipStrategy = 'fixed',
  ...props
}: Omit<TooltipAriaDisabledProps<ActionMenuItemButtonProps>, 'children'> &
  Pick<
    ActionMenuItemButtonProps,
    'LeftIcon' | 'RightIcon' | 'ref' | 'variant' | 'children' | 'size'
  >) => (
  <TooltipAriaDisabled {...props} tooltipStrategy={tooltipStrategy}>
    <MenuItemButton
      variant={variant}
      onClick={noop}
      LeftIcon={LeftIcon}
      RightIcon={RightIcon}
      size={size}
      ref={ref}
    >
      {children}
    </MenuItemButton>
  </TooltipAriaDisabled>
);
